import { useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { NativeSelect, Pager, SearchBox, TenantPicker } from "@/admin/components";
import { errText, platformApi } from "@/admin/api";
import { resourceApi, type FieldSpec } from "@/admin/resources/api";
import { FieldValue } from "@/admin/resources/fields";

export function useSchema(key: string) {
  return useQuery({
    queryKey: ["admin", "schema", key],
    queryFn: () => resourceApi.schema(key),
    staleTime: Infinity,
  });
}

export default function ResourceListPage() {
  const { resource = "" } = useParams();
  // Keyed so switching sections starts with fresh filters and page 1.
  return <ResourceList key={resource} resource={resource} />;
}

function ResourceList({ resource }: { resource: string }) {
  const navigate = useNavigate();
  const schema = useSchema(resource);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [params] = useSearchParams();
  // A link can open the list already filtered, e.g. a tenant's own ledger.
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    params.forEach((v, k) => {
      if (k !== "tenant_name" && v) out[k] = v;
    });
    return out;
  });
  const [tenantName, setTenantName] = useState(params.get("tenant_name") ?? "");

  const q = useQuery({
    queryKey: ["admin", "rows", resource, page, search, filters],
    queryFn: () => resourceApi.list(resource, { page, search: search || undefined, ...filters }),
    enabled: !!schema.data,
    placeholderData: keepPreviousData,
  });

  if (schema.isLoading) return <Skeleton className="h-48 w-full" />;
  if (!schema.data) return <p className="text-sm text-muted-foreground">This section doesn't exist.</p>;
  const s = schema.data;
  const columns = s.fields.filter((f) => f.list);
  const setFilter = (k: string, v: string) => {
    setFilters((f) => {
      const next = { ...f };
      if (v) next[k] = v;
      else delete next[k];
      return next;
    });
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{s.title}</h1>
          <p className="text-sm text-muted-foreground">{s.description}</p>
        </div>
        <div className="flex gap-2">
        {resource === "assistant-templates" && <ImportDocuments />}
        {s.create && (
          <Button asChild>
            <Link to={`/admin/${resource}/new`}>
              <Plus className="mr-2 h-4 w-4" /> Add {s.singular.toLowerCase()}
            </Link>
          </Button>
        )}
        </div>
      </div>

      {(s.search || s.filters.length > 0) && (
        <div className="flex flex-wrap items-center gap-3">
          {s.search && (
            <SearchBox
              value={search}
              onChange={(v) => {
                setSearch(v);
                setPage(1);
              }}
              placeholder="Search"
            />
          )}
          {s.filters.map((name) => {
            const field = s.fields.find((f) => f.name === name);
            if (!field) return null;
            return (
              <Filter
                key={name}
                field={field}
                value={filters[name] ?? ""}
                tenantName={tenantName}
                onChange={(v, label) => {
                  if (field.type === "tenant") setTenantName(label ?? "");
                  setFilter(name, v);
                }}
              />
            );
          })}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (q.data?.results ?? []).length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Nothing here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    {columns.map((c) => (
                      <th key={c.name} className="whitespace-nowrap px-4 py-3 font-medium">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {q.data!.results.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                      onClick={() => navigate(`/admin/${resource}/${row.id}`)}
                    >
                      {columns.map((c) => (
                        <td key={c.name} className="px-4 py-3">
                          <FieldValue field={c} row={row} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager data={q.data} onPage={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}

function Filter({
  field,
  value,
  tenantName,
  onChange,
}: {
  field: FieldSpec;
  value: string;
  tenantName: string;
  onChange: (v: string, label?: string) => void;
}) {
  if (field.type === "tenant") {
    return (
      <div className="w-72">
        <TenantPicker
          value={value ? { id: value, name: tenantName || "Selected tenant" } : null}
          onChange={(t) => onChange(t?.id ?? "", t?.name)}
          allowNone
        />
      </div>
    );
  }
  if (field.type === "bool") {
    return (
      <NativeSelect value={value} onChange={(v) => onChange(v)} className="w-40">
        <option value="">{field.label}: any</option>
        <option value="true">{field.label}: yes</option>
        <option value="false">{field.label}: no</option>
      </NativeSelect>
    );
  }
  return (
    <NativeSelect value={value} onChange={(v) => onChange(v)} className="w-44">
      <option value="">{field.label}: all</option>
      {field.choices?.map(([c, l]) => (
        <option key={c} value={c}>
          {l}
        </option>
      ))}
    </NativeSelect>
  );
}

/** Upload prompt documents (.docx, .md, .txt); each becomes a draft template
 *  to review, then "Publish to all tenants". */
function ImportDocuments() {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: platformApi.importTemplates,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["admin", "rows", "assistant-templates"] });
      if (r.created.length) toast.success(`Imported ${r.created.length} draft template(s): ${r.created.join(", ")}. Review each, then publish.`);
      if (r.failed.length) toast.error(`Couldn't read: ${r.failed.join(", ")}. Supported: .docx, .md, .txt.`);
    },
    onError: (e) => toast.error(errText(e, "Import failed.")),
  });
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        accept=".docx,.md,.txt"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) upload.mutate(files);
          e.target.value = "";
        }}
      />
      <Button variant="outline" disabled={upload.isPending} onClick={() => input.current?.click()}>
        <Upload className="mr-2 h-4 w-4" /> {upload.isPending ? "Importing…" : "Import documents"}
      </Button>
    </>
  );
}
